import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, ConfirmDialog } from '@/shared/ui';
import { useToast } from '@/shared/ui/Toast';
import { adSettingsApi } from '@/modules/settings/api/adSettingsApi';
import { adSettingsKeys } from '@/modules/settings/queryKeys';
import { useCanEditAdSettings } from '@/modules/settings/hooks';
import type { ContentPublication } from '../types';
import { adsChangeStatus, undoOutcomeWords } from '../utils/adsChangeStatus';
import styles from '../pages/TasksPage.module.css';

/**
 * An approved ads change after approval: applying, made in Google Ads (with Undo), not applied because Google or the
 * data moved on (it is drafted again), or failed (Try again). Undo is its own change: confirmed here, checked against
 * the live value, read back and logged like the change itself.
 */
export function AdsChangeStatus({
  publication,
  taskId,
  locationId,
  busy,
  onRetry,
}: {
  publication?: ContentPublication;
  taskId: string;
  locationId: string;
  busy: boolean;
  onRetry?: () => void;
}) {
  const status = adsChangeStatus(publication);
  const canUndo = useCanEditAdSettings();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const key = adSettingsKeys.appliedChange(locationId, taskId);
  const applied = useQuery({
    queryKey: key,
    queryFn: () => adSettingsApi.appliedChange(locationId, taskId),
    enabled: status.kind === 'applied' && !!locationId,
  });
  const undo = useMutation({
    mutationFn: (id: string) => adSettingsApi.undoAppliedChange(locationId, id),
    onSuccess: (outcome) => {
      if (outcome.status === 'applied') toast.success(undoOutcomeWords(outcome));
      else toast.error(undoOutcomeWords(outcome));
      setConfirming(false);
      void queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (err: Error) => toast.error(err.message ?? 'Could not undo the change.'),
  });

  if (status.kind === 'applying' || status.kind === 'not_applied')
    return <div className={styles.noteBar}>{status.text}</div>;
  if (status.kind === 'failed')
    return (
      <div className={styles.actions}>
        <span className={styles.tiny}>{status.text}</span>
        {onRetry && (
          <Button variant="secondary" size="sm" disabled={busy} onClick={onRetry}>
            {busy ? 'Retrying…' : 'Try again'}
          </Button>
        )}
      </div>
    );
  const row = applied.data;
  return (
    <div className={styles.actions}>
      <span className={styles.tiny}>
        {row?.undo ? `${status.text} Undone: ${row.undo.summary}.` : status.text}
      </span>
      {canUndo && row && row.status === 'applied' && !row.undo && (
        <Button
          variant="secondary"
          size="sm"
          disabled={undo.isPending}
          onClick={() => setConfirming(true)}
        >
          Undo this change
        </Button>
      )}
      <ConfirmDialog
        isOpen={confirming}
        title="Undo this change in Google Ads?"
        message={`This sets back what was changed (${row?.summary ?? 'the change'}) to the values Google had before. If someone changed it since, nothing is changed.`}
        confirmLabel="Undo the change"
        isLoading={undo.isPending}
        onConfirm={() => row && undo.mutate(row.id)}
        onClose={() => setConfirming(false)}
      />
    </div>
  );
}
